import { IntlTFunc } from 'next-intl';

import { SimpleProductFragment, ProductBanner } from '@/fo/api/magento';
import { utilDecodeHtmlEntities } from '@/fo/util/decode-html-entities';

import { RATING_SCALE } from '../constants';
import { Categories, MappedProduct, Option, ProductsMindbox } from '../types';
import {
  attributesMapper,
  mediaGalleryMapper,
  breadcrumbsMapper,
  labelsBrandMapper,
  labelsMapper,
  imageMapper,
  getLastLevelCategory,
  getFirstLevelCategory,
  expertImageMapper,
  getProductGroupName
} from '../utils';

export const utilMapSimpleProductData = (
  product: SimpleProductFragment,
  t: IntlTFunc,
  mindboxProducts?: ProductsMindbox
): MappedProduct => {
  const {
    url_key,
    sku: productSku,
    price,
    ratingSummary,
    reviewCount,
    detailedRating,
    stockStatus,
    image,
    thumbnail,
    mediaGallery,
    attributes,
    banners,
    special_price,
    certificate: certificateUrl,
    description_html,
    ingredients
  } = product;

  const stock = stockStatus ?? null;

  const {
    brand,
    gammas,
    name,
    subtitle,
    shortDescription,
    usageInstructions,
    activeComponents,
    volume,
    characteristics,
    purpose,
    labels,
    effects,
    benefits,
    video,
    mindboxId,
    expert,
    structure_image,
    productGroup: productGroupRaw,
    visibility,
    brandCode,
    diagnosticLabels,
    videoExpertOpinionPreview,
    videoExpertOpinionVideo,
    videoExpertOpinionExpertUrlKey
  } = attributesMapper(attributes, t);

  const sku = productSku ?? '';
  const basePrice = price.maximum_price?.final_price.value ?? null;

  const configurableVariants: Option[] = [
    {
      sku,
      label: volume?.label,
      stockQuantity: product?.only_x_left_in_stock ?? Infinity,
      stockStatus: stock,
      isSelected: true
    }
  ];

  const productGroup = productGroupRaw
    ? {
        ...productGroupRaw,
        name: getProductGroupName(productGroupRaw)
      }
    : null;

  const mindboxProductData = mindboxProducts
    ? mindboxProducts[mindboxId || sku]
    : null;

  const firstLevelCategory =
    'categories' in product
      ? getFirstLevelCategory(product.categories as Categories)
      : null;

  const lastLevelCategory =
    'categories' in product
      ? getLastLevelCategory(product.categories as Categories)
      : null;

  const bestRating =
    detailedRating?.reduce((acc, item) => {
      if (item?.rating && item?.percent) {
        acc = item.rating > acc ? item.rating : acc;
      }
      return acc;
    }, 0) ?? 0;

  const worstRating =
    detailedRating?.reduce((acc, item) => {
      if (item?.rating && item?.percent) {
        acc = item.rating < acc || acc === 0 ? item.rating : acc;
      }

      return acc;
    }, 0) ?? 0;

  return {
    sku,
    mindboxId,
    name: utilDecodeHtmlEntities(name),
    subtitle,
    link: `/product/${url_key}`,
    shortDescription,
    specialPrice: special_price ?? null,
    basePrice: mindboxProductData ? mindboxProductData.basePrice : basePrice,
    volume: volume?.label,
    finalPrice: mindboxProductData?.finalPrice,
    firstLevelCategory,
    lastLevelCategory,
    breadcrumbs: breadcrumbsMapper(
      name,
      lastLevelCategory,
      t,
      `/product/${url_key}`
    ),
    labelsBonus: mindboxProductData?.bonusLabels ?? [],
    labelsBrand: labelsBrandMapper(brand, gammas),
    brand: brand?.label ?? '',
    brandCode: brandCode ?? '',
    catalogBrandUrl: brand?.url ?? '',
    series: (gammas && gammas[0]?.label) ?? '',
    labels: labelsMapper(
      labels || [],
      mindboxProductData?.discountLabels ?? []
    ),
    baseImage: image ? imageMapper(image) : null,
    thumbnail: thumbnail ? imageMapper(thumbnail) : null,
    structureImage: structure_image || null,
    mediaGallery: mediaGalleryMapper(mediaGallery),
    rating: Math.round(((RATING_SCALE * ratingSummary) / 100) * 10) / 10,
    bestRating,
    worstRating,
    reviewCount,
    stockStatus: stock,
    stockQuantity: product?.only_x_left_in_stock || 0,
    configurableVariants,
    effects,
    benefits,
    video: video || undefined,
    bonusPoints: mindboxProductData?.bonusPoints ?? 0,
    bonusPointsMax: mindboxProductData?.bonusPointsMax ?? 0,
    earnedBonusPoints: mindboxProductData?.earnedBonusPoints ?? 0,
    certificateUrl: certificateUrl ?? null,
    purpose,

    description_html: description_html?.trim() ?? '',
    characteristics: [
      {
        label: t('product.characteristics.article'),
        value: sku
      },
      ...characteristics
    ],
    activeComponents: activeComponents ? activeComponents.split(';') : [],
    composition: ingredients?.trim() ?? '',
    usageInstructions,
    banners: banners as ProductBanner[],
    expert,
    expertImage: expertImageMapper(mediaGallery),
    visibility: visibility,
    diagnosticLabels,

    productGroup,
    videoExpertOpinionPreview,
    videoExpertOpinionVideo,
    videoExpertOpinionExpertUrlKey
  };
};
